import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftIcon, ArrowPathIcon, TrashIcon } from "@heroicons/react/24/outline";
import { apiService } from "../../services/api";
import { Machine } from "../../types";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useAuth } from "../../contexts/AuthContext";

export default function TrashedMachinesPage() {
  const { state: authState } = useAuth();
  const isTenantAdmin = authState.user?.role === "tenant_admin";
  const [machines, setMachines] = useState<Machine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadTrashedMachines = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getTrashedMachines();
      setMachines(response.data);
    } catch (err) {
      console.error("Error loading trashed machines:", err);
      setError("Erro ao carregar máquinas excluídas");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrashedMachines();
  }, [loadTrashedMachines]);

  const handleRestore = async () => {
    if (!restoreId) return;

    try {
      setIsRestoring(true);
      await apiService.restoreMachine(restoreId);
      await loadTrashedMachines();
      setRestoreId(null);
    } catch (err) {
      console.error("Error restoring machine:", err);
      alert("Erro ao restaurar máquina");
    } finally {
      setIsRestoring(false);
    }
  };

  const handleForceDelete = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);
      await apiService.forceDeleteMachine(deleteId);
      await loadTrashedMachines();
      setDeleteId(null);
    } catch (err: any) {
      console.error("Error deleting machine permanently:", err);
      alert(err.response?.data?.message || "Erro ao excluir máquina permanentemente");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/machines"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Voltar
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Máquinas Excluídas
          </h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-700">{error}</div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg dark:bg-gray-800">
        {machines.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            Nenhuma máquina na lixeira
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {machines.map((machine) => (
              <li key={machine.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                      {machine.name || machine.agent_id}
                    </h4>
                    <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <span className="truncate">
                        Excluído em:{" "}
                        {machine.deleted_at
                          ? new Date(machine.deleted_at).toLocaleString("pt-BR")
                          : "N/A"}
                      </span>
                      <span className="mx-2">•</span>
                      <span className="truncate">
                        Usuário: {machine.user_name || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex space-x-2">
                    <button
                      onClick={() => setRestoreId(machine.id)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      Restaurar
                    </button>
                    {isTenantAdmin && (
                      <button
                        onClick={() => setDeleteId(machine.id)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                      >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Excluir Permanentemente
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!restoreId}
        onCancel={() => setRestoreId(null)}
        onConfirm={handleRestore}
        title="Restaurar Máquina"
        message="Tem certeza que deseja restaurar esta máquina? Ela voltará a aparecer na lista principal."
        confirmText="Restaurar"
        cancelText="Cancelar"
        type="info"
        isLoading={isRestoring}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleForceDelete}
        title="Excluir Permanentemente"
        message="Tem certeza que deseja excluir esta máquina permanentemente? Esta ação não pode ser desfeita e todos os dados serão perdidos."
        confirmText="Excluir Permanentemente"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
